import { useState } from "react";
import { emailInvite } from "@/lib/api";

interface InviteLinkShareProps {
	userId: string;
	inviteUrl: string;
}

export function InviteLinkShare({ userId, inviteUrl }: InviteLinkShareProps) {
	const [copied, setCopied] = useState(false);
	const [sending, setSending] = useState(false);
	const [sent, setSent] = useState(false);
	const [sendError, setSendError] = useState<string | null>(null);

	async function handleCopy() {
		await navigator.clipboard.writeText(inviteUrl);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	}

	async function handleEmail() {
		if (sending) return;
		setSending(true);
		setSendError(null);
		try {
			await emailInvite(userId);
			setSent(true);
			setTimeout(() => setSent(false), 2000);
		} catch (err) {
			setSendError(err instanceof Error ? err.message : "招待メールの送信に失敗しました。");
		} finally {
			setSending(false);
		}
	}

	return (
		<div>
			<p
				style={{
					margin: "0 0 8px",
					fontSize: "var(--text-caption)",
					fontWeight: 500,
					color: "var(--fg-2)",
					fontFamily: "var(--font-sans)",
				}}
			>
				招待リンク
			</p>
			<div style={{ display: "flex", gap: 8, alignItems: "center" }}>
				<code
					title={inviteUrl}
					data-tooltip-if-truncated
					style={{
						flex: 1,
						padding: "8px 12px",
						borderRadius: "var(--radius-3)",
						boxShadow: "var(--shadow-ring)",
						background: "var(--surface-2)",
						fontFamily: "var(--font-mono)",
						fontSize: "var(--text-caption)",
						color: "var(--fg-1)",
						letterSpacing: "var(--tracking-wide)",
						userSelect: "all",
						display: "block",
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
					}}
				>
					{inviteUrl}
				</code>
				<button
					type="button"
					onClick={handleCopy}
					style={{
						height: 36,
						padding: "0 14px",
						background: copied ? "var(--success-subtle)" : "var(--surface-1)",
						border: "none",
						borderRadius: "var(--radius-3)",
						boxShadow: copied ? "0 0 0 1px var(--success-line)" : "var(--shadow-ring)",
						fontSize: "var(--text-ui)",
						fontWeight: 500,
						color: copied ? "var(--success)" : "var(--fg-1)",
						fontFamily: "var(--font-sans)",
						cursor: "pointer",
						transition: "all 150ms ease",
						whiteSpace: "nowrap",
					}}
				>
					{copied ? "コピー済み" : "コピー"}
				</button>
				<button
					type="button"
					onClick={handleEmail}
					disabled={sending}
					style={{
						height: 36,
						padding: "0 14px",
						background: sent ? "var(--success-subtle)" : "var(--surface-1)",
						border: "none",
						borderRadius: "var(--radius-3)",
						boxShadow: sent ? "0 0 0 1px var(--success-line)" : "var(--shadow-ring)",
						fontSize: "var(--text-ui)",
						fontWeight: 500,
						color: sent ? "var(--success)" : "var(--fg-1)",
						fontFamily: "var(--font-sans)",
						cursor: sending ? "not-allowed" : "pointer",
						opacity: sending ? 0.7 : 1,
						transition: "all 150ms ease",
						whiteSpace: "nowrap",
					}}
				>
					{sending ? "送信中…" : sent ? "送信済み" : "メールで送る"}
				</button>
			</div>
			{sendError && (
				<p
					style={{
						margin: "8px 0 0",
						fontSize: "var(--text-caption)",
						color: "var(--danger)",
						fontFamily: "var(--font-sans)",
					}}
				>
					{sendError}
				</p>
			)}
		</div>
	);
}
